/**
 * Trunnion AI - Cron Expression Component
 * Displays cron expression in monospace with human-readable description.
 * Supports toggling between visual builder and raw cron editing.
 * Parses standard 5-field cron (minute hour dayOfMonth month dayOfWeek).
 */
/* eslint-disable react-refresh/only-export-components */

import { useState, useMemo } from 'react';
import { Code2, Eye } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';

interface CronExpressionProps {
  value: string;
  onChange: (value: string) => void;
  showToggle?: boolean;
}

const DAYS_OF_WEEK = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

/**
 * Parse a standard 5-field cron expression and return a human-readable string.
 */
export function describeCron(cron: string): string {
  if (!cron || !cron.trim()) return 'No schedule configured';

  const parts = cron.trim().split(/\s+/);
  if (parts.length !== 5) return 'Invalid cron expression';

  const [minute, hour, dayOfMonth, month, dayOfWeek] = parts;

  // Every minute
  if (minute === '*' && hour === '*' && dayOfMonth === '*' && month === '*' && dayOfWeek === '*') {
    return 'Every minute';
  }

  // Hourly at specific minute
  if (minute !== '*' && hour === '*' && dayOfMonth === '*' && month === '*' && dayOfWeek === '*') {
    const min = minute.padStart(2, '0');
    return `Every hour at :${min}`;
  }

  // Daily at specific time
  if (minute !== '*' && hour !== '*' && dayOfMonth === '*' && month === '*' && dayOfWeek === '*') {
    const h = parseInt(hour, 10);
    const m = minute.padStart(2, '0');
    const period = h >= 12 ? 'PM' : 'AM';
    const displayHour = h === 0 ? 12 : h > 12 ? h - 12 : h;
    return `Every day at ${displayHour}:${m} ${period}`;
  }

  // Weekly (specific day(s) of week)
  if (minute !== '*' && hour !== '*' && dayOfMonth === '*' && month === '*' && dayOfWeek !== '*') {
    const h = parseInt(hour, 10);
    const m = minute.padStart(2, '0');
    const period = h >= 12 ? 'PM' : 'AM';
    const displayHour = h === 0 ? 12 : h > 12 ? h - 12 : h;

    const days = dayOfWeek.split(',').map((d) => {
      const idx = parseInt(d, 10);
      return DAYS_OF_WEEK[idx] ?? d;
    });

    if (days.length === 1) {
      return `Every ${days[0]} at ${displayHour}:${m} ${period}`;
    }
    return `Every ${days.join(', ')} at ${displayHour}:${m} ${period}`;
  }

  // Monthly (specific day of month)
  if (minute !== '*' && hour !== '*' && dayOfMonth !== '*' && month === '*' && dayOfWeek === '*') {
    const h = parseInt(hour, 10);
    const m = minute.padStart(2, '0');
    const period = h >= 12 ? 'PM' : 'AM';
    const displayHour = h === 0 ? 12 : h > 12 ? h - 12 : h;
    const dom = parseInt(dayOfMonth, 10);
    const suffix = getOrdinalSuffix(dom);
    return `Monthly on the ${dom}${suffix} at ${displayHour}:${m} ${period}`;
  }

  // Specific month
  if (minute !== '*' && hour !== '*' && dayOfMonth !== '*' && month !== '*' && dayOfWeek === '*') {
    const h = parseInt(hour, 10);
    const m = minute.padStart(2, '0');
    const period = h >= 12 ? 'PM' : 'AM';
    const displayHour = h === 0 ? 12 : h > 12 ? h - 12 : h;
    const dom = parseInt(dayOfMonth, 10);
    const suffix = getOrdinalSuffix(dom);
    const monthName = MONTHS[parseInt(month, 10) - 1] ?? month;
    return `${monthName} ${dom}${suffix} at ${displayHour}:${m} ${period}`;
  }

  // Fallback: show raw
  return `Cron: ${cron}`;
}

function getOrdinalSuffix(n: number): string {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return s[(v - 20) % 10] || s[v] || s[0];
}

/**
 * Validate a 5-field cron expression. Returns null if valid, or an error string.
 */
export function validateCron(cron: string): string | null {
  if (!cron || !cron.trim()) return 'Cron expression is required';

  const parts = cron.trim().split(/\s+/);
  if (parts.length !== 5) return 'Cron must have exactly 5 fields (minute hour dayOfMonth month dayOfWeek)';

  const [minute, hour, dayOfMonth, month, dayOfWeek] = parts;

  if (!isValidCronField(minute, 0, 59)) return 'Invalid minute field (0-59)';
  if (!isValidCronField(hour, 0, 23)) return 'Invalid hour field (0-23)';
  if (!isValidCronField(dayOfMonth, 1, 31)) return 'Invalid day of month field (1-31)';
  if (!isValidCronField(month, 1, 12)) return 'Invalid month field (1-12)';
  if (!isValidCronField(dayOfWeek, 0, 6)) return 'Invalid day of week field (0-6)';

  return null;
}

function isValidCronField(field: string, min: number, max: number): boolean {
  if (field === '*') return true;

  // Handle comma-separated values
  const values = field.split(',');
  return values.every((val) => {
    // Handle ranges (e.g., 1-5)
    if (val.includes('-')) {
      const [start, end] = val.split('-').map(Number);
      return !isNaN(start) && !isNaN(end) && start >= min && end <= max && start <= end;
    }
    // Handle step values (e.g., */5)
    if (val.includes('/')) {
      const [base, step] = val.split('/');
      const stepNum = parseInt(step, 10);
      if (isNaN(stepNum) || stepNum <= 0) return false;
      return base === '*' || (!isNaN(Number(base)) && Number(base) >= min && Number(base) <= max);
    }
    // Simple number
    const num = parseInt(val, 10);
    return !isNaN(num) && num >= min && num <= max;
  });
}

/**
 * Calculate the next N run times from a cron expression.
 */
export function getNextRunTimes(cron: string, count: number = 5, fromDate?: Date): Date[] {
  const error = validateCron(cron);
  if (error) return [];

  const parts = cron.trim().split(/\s+/);
  const [minuteField, hourField, domField, monthField, dowField] = parts;

  const allowedMinutes = expandCronField(minuteField, 0, 59);
  const allowedHours = expandCronField(hourField, 0, 23);
  const allowedDaysOfMonth = expandCronField(domField, 1, 31);
  const allowedMonths = expandCronField(monthField, 1, 12);
  const allowedDaysOfWeek = expandCronField(dowField, 0, 6);

  const results: Date[] = [];
  const cursor = fromDate ? new Date(fromDate) : new Date();

  // Start from the next minute
  cursor.setSeconds(0, 0);
  cursor.setMinutes(cursor.getMinutes() + 1);

  const maxIterations = 525600; // ~1 year of minutes
  let iterations = 0;

  while (results.length < count && iterations < maxIterations) {
    iterations++;

    const month = cursor.getMonth() + 1; // 1-based
    const dom = cursor.getDate();
    const dow = cursor.getDay();
    const hour = cursor.getHours();
    const minute = cursor.getMinutes();

    if (
      allowedMonths.includes(month) &&
      allowedDaysOfMonth.includes(dom) &&
      allowedDaysOfWeek.includes(dow) &&
      allowedHours.includes(hour) &&
      allowedMinutes.includes(minute)
    ) {
      results.push(new Date(cursor));
    }

    cursor.setMinutes(cursor.getMinutes() + 1);
  }

  return results;
}

function expandCronField(field: string, min: number, max: number): number[] {
  if (field === '*') {
    return Array.from({ length: max - min + 1 }, (_, i) => min + i);
  }

  const result = new Set<number>();

  for (const part of field.split(',')) {
    if (part.includes('/')) {
      const [base, stepStr] = part.split('/');
      const step = parseInt(stepStr, 10);
      const start = base === '*' ? min : parseInt(base, 10);
      for (let i = start; i <= max; i += step) {
        result.add(i);
      }
    } else if (part.includes('-')) {
      const [startStr, endStr] = part.split('-');
      const start = parseInt(startStr, 10);
      const end = parseInt(endStr, 10);
      for (let i = start; i <= end; i++) {
        result.add(i);
      }
    } else {
      result.add(parseInt(part, 10));
    }
  }

  return Array.from(result).sort((a, b) => a - b);
}

export function CronExpression({ value, onChange, showToggle = true }: CronExpressionProps) {
  const [rawMode, setRawMode] = useState(false);
  const [rawInput, setRawInput] = useState(value);

  const description = useMemo(() => describeCron(value), [value]);
  const validationError = useMemo(() => validateCron(rawInput), [rawInput]);

  const handleRawChange = (newVal: string) => {
    setRawInput(newVal);
    if (!validateCron(newVal)) {
      onChange(newVal);
    }
  };

  return (
    <div className="space-y-2">
      {showToggle && (
        <div className="flex items-center justify-between">
          <span className="text-xs font-medium text-trunnion-slate-400 uppercase tracking-wider">
            Cron Expression
          </span>
          <Button
            variant="ghost"
            size="sm"
            icon={rawMode ? <Eye className="h-3.5 w-3.5" /> : <Code2 className="h-3.5 w-3.5" />}
            onClick={() => {
              setRawMode(!rawMode);
              setRawInput(value);
            }}
          >
            {rawMode ? 'Visual' : 'Raw'}
          </Button>
        </div>
      )}

      {rawMode ? (
        <div className="space-y-1.5">
          <Input
            value={rawInput}
            onChange={(e) => handleRawChange(e.target.value)}
            placeholder="* * * * *"
            className="font-mono text-sm"
            error={validationError ?? undefined}
            helperText="Format: minute hour dayOfMonth month dayOfWeek"
          />
        </div>
      ) : (
        <div className="rounded-lg border border-trunnion-slate-700 bg-trunnion-slate-900 px-3 py-2">
          <code className="text-sm font-mono text-trunnion-gold-500">
            {value || '* * * * *'}
          </code>
        </div>
      )}

      <p className="text-xs text-trunnion-slate-400">{description}</p>
    </div>
  );
}
